import React from 'react';
import { 
  Zap, 
  Users, 
  Clock, 
  Search, 
  Smartphone,
  CheckCircle2
} from 'lucide-react';

const BenefitsSection = () => {
  const benefits = [
    {
      icon: Zap,
      title: 'Faster Website Creation',
      description: 'AI tools help us build beautiful, functional websites in a fraction of the time.',
    },
    {
      icon: Users,
      title: 'More Leads & Inquiries',
      description: 'Optimized designs and automation capture more leads while you sleep.',
    },
    {
      icon: Clock,
      title: 'Time-Saving Automation',
      description: 'Automated chatbots, follow-ups, and notifications free up your time.',
    },
    {
      icon: Search,
      title: 'Better Google Visibility',
      description: 'SEO optimization and regular content updates help you rank higher.',
    },
    {
      icon: Smartphone,
      title: 'Modern Digital Presence',
      description: 'Mobile-first design that works perfectly on all devices.',
    },
  ];

  return (
    <section 
      className="py-24 lg:py-32 bg-slate-50"
      data-testid="benefits-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
              <CheckCircle2 className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Why Choose Us</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Benefits of Working with an{' '}
              <span className="gradient-text">AI Growth Partner</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              We're not just website developers. We're your AI growth partners, committed to helping your business thrive online with the latest technology and automation tools.
            </p>

            {/* Benefits List */}
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="benefit-item flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-100 hover:border-blue-100 hover:shadow-md transition-all duration-300"
                  data-testid={`benefit-item-${index}`}
                >
                  <div className="benefit-check w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">
                      {benefit.title}
                    </h4>
                    <p className="text-sm text-slate-600">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-blue-900/10">
              <img
                src="https://images.unsplash.com/photo-1771240730126-594a8ab66be1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGNvbnN1bHRhbnQlMjBwb3J0cmFpdCUyMG1vZGVybiUyMG9mZmljZSUyMGZyaWVuZGx5fGVufDB8fHx8MTc3MzAzNzM4NXww&ixlib=rb-4.1.0&q=85"
                alt="AI Growth Partner"
                className="w-full h-auto object-cover"
                data-testid="benefits-image"
              />
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -top-8 -left-8 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-violet-500/20 rounded-full blur-3xl" />
            
            {/* Stats Card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">500+</div>
                  <div className="text-sm text-slate-500">Happy Clients</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
